import * as React from "react";
import { NavIcon } from "@lunchboxinc/lunchbox-components-v2/dist/components";

import { Elements, ElementsThemed, Fragments, HOCs } from "components";
import { Order, Login, Logout } from "pages";
import { config, constants, Copy, images } from "utils";
import { matchesRegex } from "utils/helpers/string";
import BackButton from "../../../components/BackButton/BackButton";
import NavDrawer from "../../navDrawer";

import theme1 from "../header.module.scss";
import theme2 from "../headerTheme2.module.scss";

import type { HeaderProps } from "../../header";

const {
  Logo,
  Icon,
  Condition: { Condition: If },
} = Elements;
const { Text, View, Button } = ElementsThemed;
const { Dropdown, Drawer, Pill, LocationModal } = Fragments;

const { withTemplate, withCSSTheme } = HOCs;
const {
  ORDER_TYPES: { KIOSK },
} = constants;
const loginIcon = config?.images?.icon_login;
const restaurantIcon = config?.images?.art_logo;
const profileIcon = config?.images?.icon_profile;
const logoutIcon = config?.images?.icon_logout;
const backButton = config?.images?.button_back;

function Header({
  cartCount,
  cartIconSrc,
  css,
  diningOption,
  history,
  isCartDrawerOpen,
  isLoginDrawerOpen,
  isLogoutDrawerOpen,
  location,
  orderContext,
  orderType,
  patronContext,
  regexpsBack,
  regexpsMarketplaceLocation,
  regexpsBackCheckout,
  regexpsBackMenu,
  regexpsCart,
  regexpsLogo,
  routerLocation,
  setIsLoginDrawerOpen,
  setIsLogoutDrawerOpen,
  style,
  toggleLoginDrawer,
  toggleLogoutDrawer,
  trackerPath,
  handleAccountClick,
  isOpen,
}: HeaderProps) {
  const { views, labels, buttons } = style;
  const iconType =
    config.theme.header.nav_icon === "Type2" ? buttons.nav : labels.primary;
  const { order, setScheduledAt } = orderContext;
  const isLocationModalEnabled =
    config?.isEarlySchedulingEnabled && diningOption !== KIOSK;
  const pillProps = {
    diningOption,
    location,
    scheduleAt: order?.scheduledAt,
    setScheduledAt,
    streetName: order?.address?.street1,
    toScheduleDate: () => {
      history.replace(`${routerLocation.pathname}?isLocationModalOpen=true`);
    },
  };

  return (
    <>
      {location?.id && isLocationModalEnabled && (
        <LocationModal history={history} />
      )}
      <View
        id="header"
        type={views.background}
        Component="header"
        className={css.header}
        style={{
          backgroundImage: `${
            config?.images.web_banner
              ? `url(${config?.images.web_banner})`
              : "initial"
          }`,
        }}
      >
        <div className={css["header-wrapper"]} id="header-wrapper">
          <div className={css["header-wrapper--left"]}>
            {/* Only show the logo if on the home page or on the location page and not pocket kiosk */}
            <If
              is={
                !matchesRegex(regexpsLogo, routerLocation.pathname) ||
                (matchesRegex(regexpsLogo, routerLocation.pathname) &&
                  diningOption !== KIOSK)
              }
            >
              <div className={css.logo}>
                <a href={config.website.url}>
                  <Logo src={images?.art_logo ?? restaurantIcon} />
                </a>
              </div>
            </If>

            {location && !matchesRegex(regexpsBack, routerLocation.pathname) && (
              <div className={css.nav}>
                <If is={!config?.routes?.market && !isLocationModalEnabled}>
                  <>
                    <BackButton
                      imgSrc={images?.button_back_main ?? backButton}
                      className={css["nav-back"]}
                      onClick={() => history.push("/")}
                    />
                    {location.name ? (
                      <div className={css["nav-info"]}>
                        <Text type={labels.tertiary}>{orderType}</Text>
                        <Text type={labels.secondary}>{location.name}</Text>
                      </div>
                    ) : (
                      <div className={css["nav-info"]}>
                        <Text type={labels.tertiary}>
                          {Copy.HEADER_STATIC.GO_TO_LOCATIONS_TEXT}
                        </Text>
                      </div>
                    )}
                  </>
                </If>
              </div>
            )}
            <If is={matchesRegex(regexpsBackMenu, routerLocation.pathname)}>
              <BackButton
                imgSrc={images?.button_back_main}
                className={css["nav-back"]}
                onClick={() => history.goBack()}
              />
              <div className={css["nav-info"]}>
                <Text type={labels.secondary}>
                  {Copy.HEADER_STATIC.GO_BACK_TO_MENU_TEXT}
                </Text>
              </div>
            </If>

            <If is={matchesRegex(regexpsBackCheckout, routerLocation.pathname)}>
              <BackButton
                imgSrc={images?.button_back_main}
                className={css["nav-back"]}
                onClick={() => history.push("/")}
              />
              <div className={css["nav-info"]}>
                <Text type={labels.tertiary}>
                  {Copy.HEADER_STATIC.START_A_NEW_ORDER_TEXT}
                </Text>
              </div>
            </If>
          </div>
          <div className={css["header-wrapper--right"]}>
            <If
              is={!config.routes || (config.routes && config.routes.ordering)}
            >
              <If
                is={
                  !patronContext.isLoggedIn &&
                  !matchesRegex([/^\/sign-up/], routerLocation.pathname)
                }
              >
                <button
                  onClick={toggleLoginDrawer}
                  data-cy="login-icon"
                  type="submit"
                  className={css["header-wrapper--headerLoginButton"]}
                >
                  <NavIcon
                    iconType={iconType}
                    themeType={config.theme.header.nav_icon || "Type1"}
                    restaurantName={config.restaurant}
                    symbol="user"
                    iconSrc={config?.images?.login || loginIcon}
                  >
                    {Copy.HEADER_STATIC.LOGIN_TEXT}
                  </NavIcon>
                </button>
              </If>

              {patronContext.isLoggedIn && config.theme.account?.drawer && (
                <NavIcon
                  iconType={iconType}
                  themeType={config.theme.header.nav_icon || "Type1"}
                  restaurantName={config.restaurant}
                  symbol="user"
                  iconSrc={config?.images?.login || loginIcon}
                  datatest="account-icon"
                  onClick={handleAccountClick}
                >
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    className={css["header-wrapper--accountDropdown"]}
                  >
                    {Copy.HEADER_STATIC.ACCOUNT_TEXT}
                  </button>
                </NavIcon>
              )}

              <If
                is={patronContext.isLoggedIn && !config.theme.account?.drawer}
              >
                <Dropdown
                  className={css.dropdown}
                  trigger={
                    <NavIcon
                      iconType={iconType}
                      themeType={config.theme.header.nav_icon || "Type1"}
                      restaurantName={config.restaurant}
                      symbol="user"
                      iconSrc={config?.images?.login || loginIcon}
                      data-cy="account-icon"
                    >
                      {Copy.HEADER_STATIC.ACCOUNT_TEXT}
                    </NavIcon>
                  }
                >
                  <DropdownItem
                    text="Profile"
                    type={buttons.dropdown}
                    css={css}
                    icon={config?.images?.login || profileIcon}
                    onClick={() => history.push("/profile")}
                    data-cy="profile-icon"
                  />
                  <DropdownItem
                    text={Copy.HEADER_STATIC.LOGOUT_TEXT}
                    type={buttons.dropdown}
                    css={css}
                    icon={logoutIcon}
                    onClick={toggleLogoutDrawer}
                    data-cy="logout-icon"
                  />
                </Dropdown>
              </If>
            </If>
            <If is={!matchesRegex(regexpsCart, routerLocation.pathname)}>
              <NavDrawer
                iconType={iconType}
                text={
                  orderContext.order.diningOption === KIOSK
                    ? Copy.HEADER_STATIC.CHECKOUT_TEXT
                    : Copy.HEADER_STATIC.CART_TEXT
                }
                symbol="cart"
                icon={cartIconSrc}
                isOpen={isCartDrawerOpen}
                css={css}
                cartCount={cartCount}
              >
                {({ close }) => (
                  <Order
                    orderContext={orderContext}
                    patronContext={patronContext}
                    history={history}
                    location={routerLocation}
                    close={close}
                    isLoggedIn={patronContext.isLoggedIn}
                    isEmployee={patronContext.patron.isEmployee}
                  />
                )}
              </NavDrawer>
            </If>

            <Drawer.ResponsiveDrawer
              isOpen={isLoginDrawerOpen}
              onChange={setIsLoginDrawerOpen}
              ariaLabel="Login"
            >
              <Login
                onComplete={() => setIsLoginDrawerOpen(false)}
                trackerPath={trackerPath}
              />
            </Drawer.ResponsiveDrawer>

            <Drawer.ResponsiveDrawer
              isOpen={isLogoutDrawerOpen}
              onChange={setIsLogoutDrawerOpen}
              ariaLabel="Logout"
            >
              <Logout
                onConfirm={() => {
                  setIsLogoutDrawerOpen(false);
                  patronContext.logout();
                }}
                onCancel={() => setIsLogoutDrawerOpen(false)}
              />
            </Drawer.ResponsiveDrawer>
          </div>
        </div>
        <If is={!config?.routes?.market && isLocationModalEnabled}>
          <div className={css.pill}>
            <If
              is={matchesRegex(
                regexpsMarketplaceLocation,
                routerLocation.pathname,
              )}
            >
              <Pill {...pillProps} />
            </If>
          </div>
        </If>
      </View>
    </>
  );
}

const cssTheme = () => {
  switch (config.theme.header.theme) {
    case "Theme2":
      return theme2;
    default:
      return theme1;
  }
};

interface DropdownItemProps {
  text: string;
  type: string;
  css: Record<string, string>;
  icon: string;
  onClick: () => void;
  "data-cy": string;
}

const DropdownItem = withCSSTheme(
  ({ text, icon, css, ...props }: DropdownItemProps) => (
    <Button className={css["dropdown-item"]} {...props}>
      {text}
      {icon && <Icon src={icon} />}
    </Button>
  ),
  cssTheme(),
);

export default withTemplate(withCSSTheme(Header, cssTheme()), "main");
